import React from 'react'

import {
  ArrowLeftRightIcon,
  BadgeCheckIcon,
  EyeIcon,
  MergeIcon,
  ScanLineIcon,
  SplitIcon,
  UnlinkIcon,
} from 'lucide-react'

import type { CompanyCertificateActivityItem } from 'services/companyCertificateActivityApi'

import type { ActivityRow } from './Activity'

/**
 * One certificate-activity item → one row of the shared `Activity` timeline.
 *
 * EXTRACTED on the second hand-roll (§36). This mapping was written for Home's Recent activity
 * card; the full Certificate activity page is the same feed at a different length, so a second
 * copy would have been two action→label maps and two action→icon maps to keep in step — and
 * the failure mode is silent, because an unmapped action still renders, just wrongly.
 */

/**
 * How each action type reads in a sentence, from the BRAND's point of view rather than the
 * log's. The raw types are internal names (`admin_transfer`, `init_transfer`, `public_access`)
 * and several are near-synonyms a brand operator has no reason to distinguish — the transfer
 * lifecycle alone has six. They collapse here into the handful of things that happened.
 *
 * An unmapped type degrades to its own name with underscores stripped, so a new server-side
 * action reads awkwardly rather than rendering blank.
 */
const ACTION_LABEL: Partial<Record<string, string>> = {
  // Every value in `enum_certificate_action_type` is mapped, taken from the enum rather than
  // from what happens to appear in seed data — an unmapped type falls through to its own raw
  // name and reads as a grammatical error ("An owner return Cartier Love Bracelet"), which is
  // exactly how `return` was caught.
  create_certificate: 'issued',
  update_certificate: 'updated',
  update: 'updated',
  scan: 'scanned',
  invalid_scan: 'failed a scan of',
  scan_transfer: 'scanned to transfer',
  public_access: 'viewed',
  assign_tag: 'assigned a tag to',
  replace_tag: 'replaced the tag on',
  assign_product: 'assigned a product to',
  init_transfer: 'started a transfer of',
  confirm_transfer: 'confirmed a transfer of',
  accept_transfer: 'accepted a transfer of',
  cancel_transfer: 'cancelled a transfer of',
  cancel_initiated_transfer: 'cancelled a transfer of',
  request_cancel_transfer: 'asked to cancel a transfer of',
  confirm_request_cancel_transfer: 'approved cancelling a transfer of',
  reject_request_cancel_transfer: 'declined cancelling a transfer of',
  admin_transfer: 'transferred',
  admin_cancel_transfer: 'cancelled a transfer of',
  // Owner-reported condition. Phrased as a report, not an event, because the brand did not
  // do it and nothing in Vera changed hands.
  loose: 'reported as lost',
  stole: 'reported as stolen',
  return: 'returned',
  add_to_group: 'added to a group',
  remove_from_group: 'removed from a group',
  split_group: 'split a group containing',
  CertificateStatusChange: 'changed the status of',
}

/** Each consumer owns its own action→icon mapping; the shared component stays generic. */
const ACTION_ICON: Partial<Record<string, React.ReactNode>> = {
  scan: React.createElement(ScanLineIcon),
  public_access: React.createElement(EyeIcon),
  create_certificate: React.createElement(BadgeCheckIcon),
  admin_transfer: React.createElement(ArrowLeftRightIcon),
  init_transfer: React.createElement(ArrowLeftRightIcon),
  accept_transfer: React.createElement(ArrowLeftRightIcon),
  confirm_transfer: React.createElement(ArrowLeftRightIcon),
  admin_cancel_transfer: React.createElement(ArrowLeftRightIcon),
  cancel_initiated_transfer: React.createElement(ArrowLeftRightIcon),
  request_cancel_transfer: React.createElement(ArrowLeftRightIcon),
  // VRG-231 review round 3 — these three were ALL `LayersIcon`: three distinct
  // events sharing one glyph, so the feed could not be scanned for "when did this
  // leave its group". They now use the same action vocabulary as the kebabs —
  // Merge to join, Unlink to leave, Split to dissolve — and `LayersIcon` is gone
  // from the certificates domain entirely (it collided with the indivisible
  // divisibility badge; see DivisibilityOptionPicker's header comment).
  add_to_group: React.createElement(MergeIcon),
  remove_from_group: React.createElement(UnlinkIcon),
  split_group: React.createElement(SplitIcon),
  scan_transfer: React.createElement(ScanLineIcon),
  invalid_scan: React.createElement(ScanLineIcon),
  cancel_transfer: React.createElement(ArrowLeftRightIcon),
  confirm_request_cancel_transfer: React.createElement(ArrowLeftRightIcon),
  reject_request_cancel_transfer: React.createElement(ArrowLeftRightIcon),
  return: React.createElement(ArrowLeftRightIcon),
  update_certificate: React.createElement(BadgeCheckIcon),
  update: React.createElement(BadgeCheckIcon),
  assign_tag: React.createElement(BadgeCheckIcon),
  replace_tag: React.createElement(BadgeCheckIcon),
  assign_product: React.createElement(BadgeCheckIcon),
  loose: React.createElement(EyeIcon),
  stole: React.createElement(EyeIcon),
  CertificateStatusChange: React.createElement(BadgeCheckIcon),
}

/**
 * A person's name for the feed — never their raw email address.
 *
 * Falling back to `email` puts a contactable address into a list teammates (and later,
 * exports) can see, and it reads badly. Plenty of seeded and invited admins have no name set,
 * so this is the COMMON case here. The local part is recognisable to colleagues without being
 * an address.
 */
const adminName = (item: CompanyCertificateActivityItem): string => {
  const actor = item.actor
  if (!actor) return 'A teammate'
  const name = [actor.first_name, actor.last_name].filter(Boolean).join(' ').trim()
  return name || actor.email.split('@')[0] || 'A teammate'
}

/**
 * `result` is rendered through an HTML parser by the shared component, so anything
 * interpolated into it MUST be escaped.
 *
 * Product titles are brand-authored free text and reach this string directly.
 */
const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')

export const toCertificateActivityRow = (
  item: CompanyCertificateActivityItem,
  viewerId?: string
): ActivityRow => {
  const isViewer = item.user_type === 'admin' && !!viewerId && item.actor?.id === viewerId

  // "You" for the viewer's own rows: in a small brand's feed most admin rows ARE theirs, and
  // reading your own name back at you is slower than the pronoun. `id` is deliberately
  // omitted for non-admins so the shared component renders them unlinked — there is no user
  // page for an anonymous scanner.
  const label = isViewer
    ? 'You'
    : item.user_type === 'admin'
      ? adminName(item)
      : item.user_type === 'customer'
        ? 'An owner'
        : 'Someone'

  return {
    user: {
      id: item.user_type === 'admin' ? (item.actor?.id ?? '') : '',
      full_name: label,
      avatar: null,
      role: null,
    },
    action: ACTION_LABEL[item.type] ?? item.type.replace(/_/g, ' '),
    result: `<span>${escapeHtml(item.certificate.name ?? 'a certificate')}</span>`,
    date: item.created_at,
    icon: ACTION_ICON[item.type],
  }
}
